import { z } from 'zod';
/**
 * Codes d'erreur stables de l'API (docs/api/API_GUIDELINES.md §5).
 * Le message est localisé côté serveur ; le code ne change jamais.
 */
export const ErrorCode = z.enum([
    // 400
    'VALIDATION_ERROR',
    // 401
    'UNAUTHENTICATED',
    'SESSION_EXPIRED',
    'MFA_REQUIRED',
    'INVALID_CREDENTIALS',
    'ACCOUNT_LOCKED',
    'ACCOUNT_DISABLED',
    'CSRF_TOKEN_INVALID',
    // 403
    'PERMISSION_DENIED',
    // 404
    'NOT_FOUND',
    // 409
    'CONFLICT',
    'OPTIMISTIC_LOCK_CONFLICT',
    'EMAIL_ALREADY_USED',
    'ROLE_IN_USE',
    'SYSTEM_ROLE_IMMUTABLE',
    'RESERVATION_INVALID_TRANSITION',
    'RESOURCE_NOT_AVAILABLE',
    'INVOICE_COMPLIANCE_DOCUMENTS_MISSING',
    'INVOICE_IMMUTABLE',
    'INVOICE_ALREADY_EXISTS',
    'BILLING_DOCUMENT_NOT_EDITABLE',
    'PROFORMA_NOT_ISSUED',
    'CREDIT_NOTE_EXCEEDS_INVOICE',
    'PAYMENT_EXCEEDS_BALANCE',
    'PAYMENT_ALREADY_REVERSED',
    'PAYMENT_ALLOCATION_EXCEEDS_AMOUNT',
    'REFUND_EXCEEDS_PAYMENT',
    'CASH_SESSION_ALREADY_OPEN',
    'CASH_SESSION_NOT_OPEN',
    'CASH_SESSION_CLOSED',
    /** Check-out refusé : solde restant dû sans motif ni permission (R-010). */
    'BALANCE_DUE',
    // 413 / 415
    'UPLOAD_TOO_LARGE',
    'UPLOAD_UNSUPPORTED_TYPE',
    // 422
    'PASSWORD_POLICY_VIOLATION',
    'INVITATION_INVALID',
    'TOKEN_INVALID',
    'PERMISSION_ESCALATION',
    /** Confirmation refusée : aucune avance enregistrée (R-013, D-020/D-022). */
    'DEPOSIT_REQUIRED',
    /**
     * Même `Idempotency-Key` rejouée avec un corps différent
     * (SECURITY_ARCHITECTURE §5.6) : la première réponse reste seule valable.
     */
    'IDEMPOTENCY_KEY_REUSED',
    // 429
    'RATE_LIMITED',
    // 500
    'INTERNAL_ERROR',
    // 501
    'NOT_IMPLEMENTED',
]);
export const ApiError = z.object({
    error: z.object({
        code: ErrorCode,
        message: z.string(),
        details: z.record(z.unknown()).optional(),
        traceId: z.string().optional(),
    }),
});
export const HTTP_STATUS_BY_CODE = {
    VALIDATION_ERROR: 400,
    UNAUTHENTICATED: 401,
    SESSION_EXPIRED: 401,
    MFA_REQUIRED: 401,
    INVALID_CREDENTIALS: 401,
    ACCOUNT_LOCKED: 401,
    ACCOUNT_DISABLED: 401,
    CSRF_TOKEN_INVALID: 403,
    PERMISSION_DENIED: 403,
    NOT_FOUND: 404,
    CONFLICT: 409,
    OPTIMISTIC_LOCK_CONFLICT: 409,
    EMAIL_ALREADY_USED: 409,
    ROLE_IN_USE: 409,
    SYSTEM_ROLE_IMMUTABLE: 409,
    RESERVATION_INVALID_TRANSITION: 409,
    RESOURCE_NOT_AVAILABLE: 409,
    INVOICE_COMPLIANCE_DOCUMENTS_MISSING: 409,
    INVOICE_IMMUTABLE: 409,
    INVOICE_ALREADY_EXISTS: 409,
    BILLING_DOCUMENT_NOT_EDITABLE: 409,
    PROFORMA_NOT_ISSUED: 409,
    CREDIT_NOTE_EXCEEDS_INVOICE: 422,
    PAYMENT_EXCEEDS_BALANCE: 409,
    PAYMENT_ALREADY_REVERSED: 409,
    PAYMENT_ALLOCATION_EXCEEDS_AMOUNT: 422,
    REFUND_EXCEEDS_PAYMENT: 422,
    CASH_SESSION_ALREADY_OPEN: 409,
    CASH_SESSION_NOT_OPEN: 409,
    CASH_SESSION_CLOSED: 409,
    BALANCE_DUE: 409,
    UPLOAD_TOO_LARGE: 413,
    UPLOAD_UNSUPPORTED_TYPE: 415,
    PASSWORD_POLICY_VIOLATION: 422,
    INVITATION_INVALID: 422,
    TOKEN_INVALID: 422,
    PERMISSION_ESCALATION: 422,
    DEPOSIT_REQUIRED: 422,
    IDEMPOTENCY_KEY_REUSED: 422,
    RATE_LIMITED: 429,
    INTERNAL_ERROR: 500,
    NOT_IMPLEMENTED: 501,
};
//# sourceMappingURL=errors.js.map